import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { apiRequest } from '../utils/auth';

export default function CustomerRegistration() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    companyName: '',
    rfqIdPrefix: '',
    city: '',
    state: '',
    country: '',
    businessType: '',
    saleType: '',
    requirements: [],
    contactPerson: '',
    mobile: '',
    website: '',
    email: '',
  });

  const requirementOptions = ['Forging', 'Machining', 'Welding', 'Assembly', 'Heat Treatment'];

  const handleRequirementToggle = (req) => {
    setFormData((prev) => ({
      ...prev,
      requirements: prev.requirements.includes(req)
        ? prev.requirements.filter((r) => r !== req)
        : [...prev.requirements, req],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await apiRequest('/api/customers', {
        method: 'POST',
        body: JSON.stringify({
          company_name: formData.companyName.toUpperCase().trim(),
          rfq_id_prefix: formData.rfqIdPrefix.toUpperCase().trim(),
          city: formData.city,
          state: formData.state,
          country: formData.country,
          business_type: formData.businessType,
          requirements: formData.requirements,
          sale_type: formData.saleType,
          contact_person: formData.contactPerson,
          mobile: formData.mobile,
          website: formData.website,
          email: formData.email,
        }),
      });
      
      toast.success(`Customer registered successfully! Code: ${response.customer_code}`);
      
      setTimeout(() => {
        navigate(`/rfq-registration?customerCode=${response.customer_code}`);
      }, 1000);
    } catch (error) {
      toast.error(error.message || 'Failed to register customer');
    }
  };

  return (
    <Layout>
      <div data-testid="customer-registration-page" className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-[#0A2540]">Customer Registration</h2>
          <p className="text-sm text-gray-600 mt-1">Register a new customer in the system</p>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="p-6 mb-6 border-gray-200">
            <h3 className="text-lg font-semibold text-[#0A2540] mb-4">Company Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="companyName">Name of Company *</Label>
                <Input
                  id="companyName"
                  data-testid="input-company-name"
                  required
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  className="mt-1"
                />
                {formData.companyName && (
                  <p className="text-xs text-blue-600 mt-1">
                    Will be saved as: <strong>{formData.companyName.toUpperCase()}</strong>
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="rfqIdPrefix">RFQ ID Prefix * (e.g., HYS-, TOSH-)</Label>
                <Input
                  id="rfqIdPrefix"
                  data-testid="input-rfq-prefix"
                  required
                  value={formData.rfqIdPrefix}
                  onChange={(e) => setFormData({ ...formData, rfqIdPrefix: e.target.value })}
                  className="mt-1"
                  placeholder="Enter prefix for RFQ IDs"
                />
                {formData.rfqIdPrefix && (
                  <p className="text-xs text-blue-600 mt-1">
                    RFQ IDs will be: <strong>{formData.rfqIdPrefix.toUpperCase()}0001</strong>, <strong>{formData.rfqIdPrefix.toUpperCase()}0002</strong>...
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  data-testid="input-city"
                  required
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="state">State *</Label>
                <Input
                  id="state"
                  data-testid="input-state"
                  required
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="country">Country *</Label>
                <Input
                  id="country"
                  data-testid="input-country"
                  required
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
          </Card>

          <Card className="p-6 mb-6 border-gray-200">
            <h3 className="text-lg font-semibold text-[#0A2540] mb-4">Business Details</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="businessType">Type of Business *</Label>
                <Select required value={formData.businessType} onValueChange={(val) => setFormData({ ...formData, businessType: val })}>
                  <SelectTrigger data-testid="select-business-type" className="mt-1">
                    <SelectValue placeholder="Select business type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Automotive</SelectItem>
                    <SelectItem value="non-auto">Non-Automotive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Type of Requirement *</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                  {requirementOptions.map((req) => (
                    <div key={req} className="flex items-center space-x-2">
                      <Checkbox
                        id={req}
                        data-testid={`checkbox-${req.toLowerCase()}`}
                        checked={formData.requirements.includes(req)}
                        onCheckedChange={() => handleRequirementToggle(req)}
                      />
                      <label htmlFor={req} className="text-sm cursor-pointer">
                        {req}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="saleType">Type of Sale *</Label>
                <Select required value={formData.saleType} onValueChange={(val) => setFormData({ ...formData, saleType: val })}>
                  <SelectTrigger data-testid="select-sale-type" className="mt-1">
                    <SelectValue placeholder="Select sale type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="domestic">Domestic</SelectItem>
                    <SelectItem value="export">Export</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          <Card className="p-6 mb-6 border-gray-200">
            <h3 className="text-lg font-semibold text-[#0A2540] mb-4">Contact Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contactPerson">Contact Person Name *</Label>
                <Input
                  id="contactPerson"
                  data-testid="input-contact-person"
                  required
                  value={formData.contactPerson}
                  onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="mobile">Mobile Number *</Label>
                <Input
                  id="mobile"
                  data-testid="input-mobile"
                  type="tel"
                  required
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  data-testid="input-website"
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="email">Email ID *</Label>
                <Input
                  id="email"
                  data-testid="input-email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
          </Card>

          <div className="flex gap-4">
            <Button
              type="submit"
              data-testid="submit-customer-button"
              className="bg-[#0A2540] hover:bg-[#0d2f52] text-white"
            >
              Register Customer
            </Button>
            <Button
              type="button"
              data-testid="cancel-button"
              variant="outline"
              onClick={() => navigate('/rfq')}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}